import * as React from 'react';
import {ErrorMessage} from '../error-message';
import {Label} from '../label';
import {cn} from '../lib/cn';
import {Switch} from '../switch';
import type {SwitchOption} from './types';

export interface SwitchGroupProps {
  options: SwitchOption[];
  value?: string[];
  onChange?: (selectedValues: string[]) => void;
  disabled?: boolean;
  className?: string;
  showSelectAll?: boolean;
  selectAllLabel?: string;
  error?: string;
}

export const SwitchGroup = React.forwardRef<HTMLDivElement, SwitchGroupProps>(
  (
    {options, value = [], onChange, disabled = false, className, error, ...props},
    ref,
  ) => {
    const handleOptionChange = (optionValue: string, checked: boolean) => {
      if (!onChange) return;

      if (checked) {
        onChange([...value, optionValue]);
        return;
      }

      onChange(value.filter(selectedValue => selectedValue !== optionValue));
    };

    return (
      <div ref={ref} className={cn('w-full space-y-4', className)} {...props}>
        {options.map(option => (
          <div key={option.value} className="flex items-center justify-between gap-4">
            <div className="flex flex-col">
              <Label
                className={cn(
                  'text-md cursor-pointer font-medium text-[var(--chekin-color-brand-navy)]',
                  (disabled || option.disabled) && 'opacity-50',
                )}
              >
                {option.label}
                {option.description && (
                  <span
                    className={cn(
                      'mt-1 block text-sm font-normal text-[var(--chekin-color-gray-1)]',
                      (disabled || option.disabled) && 'opacity-50',
                    )}
                  >
                    ({option.description})
                  </span>
                )}
              </Label>
            </div>
            <Switch
              value={value.includes(option.value)}
              onChange={checked => handleOptionChange(option.value, checked)}
              disabled={disabled || option.disabled}
              size="lg"
            />
          </div>
        ))}
        {error && <ErrorMessage disabled={disabled}>{error}</ErrorMessage>}
      </div>
    );
  },
);

SwitchGroup.displayName = 'SwitchGroup';
